import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach access token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    // Decode token to check expiration (without verification)
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const expTimestamp = payload.exp;
        const nowTimestamp = Math.floor(Date.now() / 1000);
        const secondsLeft = expTimestamp - nowTimestamp;
        
        if (secondsLeft < 60) {
          console.warn(`⏰ Access token expira em ${secondsLeft}s - pode expirar durante a requisição`);
        }
      }
    } catch (e) {
      // Silently ignore decode errors
    }
    
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        console.error('🔐 Ambos os tokens expiraram. Fazendo logout...');
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }

      try {
        console.log('🔄 Tentando renovar token de acesso...');
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access, refresh } = response.data;
        localStorage.setItem('access_token', access);
        if (refresh) {
          localStorage.setItem('refresh_token', refresh);
        }

        console.log('✅ Token renovado com sucesso');
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ Falha ao renovar token:', refreshError);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      }
    }

    // Log 401 errors with details
    if (error.response?.status === 401) {
      console.error('❌ 401 Unauthorized:', {
        url: error.config?.url,
        method: error.config?.method,
        hasAuth: !!error.config?.headers?.Authorization,
        accessToken: localStorage.getItem('access_token') ? '(exists)' : '(missing)',
        refreshToken: localStorage.getItem('refresh_token') ? '(exists)' : '(missing)',
        response: error.response?.data,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
